import React, { Component, createElement } from 'react';
import classnames from 'classnames';
import ResizeObserver from 'rc-resize-observer';
import { getSysFieldid } from '@platform/api';
import { calcFixedVirtualInfo, calcVariableVirtualInfo } from '../utils';
import ColGroup from './ColGroup';

// @forwardRef
export default class TableBody extends Component {
    innerRowsHeight = {};

    constructor(props) {
        super(props);
        this.state = { 
            scrollTop: 0,
            currentIndex: -1,
        };
        this.startY = 0;
        this.startHeight = 0;
    }
    
    render() {
        let { bodyStyle, tableRef } = this.props;
        
        this.spanManager = new SpanManager();

        return <div className="table-wrapper table-next-body-wrapper u-table-body-outer" /* 最后一个是自动化标记 */>
            <div
                ref={tableRef}
                className="table-wrapper table-next-body-inner-wrapper" 
                style={bodyStyle}
                // style={{ maxHeight: isNaN(maxBodyHeight) ? 'none' : maxBodyHeight }}
                // onScroll={this.virtualScroll}
            >
                {bodyStyle?.height ? <table className="table-next-body">
                    <ColGroup {...this.props}></ColGroup>
                    <tbody>{this.getRenderRows().map(render => render())}</tbody>
                </table> : null}
            </div>
            {this.renderVerticalScrollBar()}
        </div>;
    }

    componentDidUpdate() {
        let { tableRef, onScrollBarStatusChange } = this.props,
            dom = tableRef.current;
        
        if (!dom) {
            return;
        }

        let scrollBarStatus;
        if (dom.clientHeight < dom.scrollHeight) {
            scrollBarStatus = true;
        } else {
            scrollBarStatus = false;
        }
        if (this.lastScrollBarStatus !== scrollBarStatus) {
            onScrollBarStatusChange(scrollBarStatus);
            this.lastScrollBarStatus = scrollBarStatus;
        }
    }

    getRenderRows = () => {
        let { mergeCell, rows, flex, bodyStyle = {}, columnInfo, tableBodyWidth, locale } = this.props;

        if (!rows.length) {
            this.tableHeight = flex ? Math.max(bodyStyle.height, 60) : (bodyStyle.height === 'auto' ? bodyStyle.minHeight || 60 : bodyStyle.height);
            return [() => <tr 
                key='_empty_table_' 
                className='empty-table'
            >
                <td 
                    colSpan={columnInfo.leaf.columns.length}
                    style={{ height: this.tableHeight }}
                >
                    {/* 左右border，-2px */}
                    <div className="empty-table-wrapper" style={{ width: tableBodyWidth - 2 }}>
                        <i className='empty-table-icon'></i>
                        <span className='empty-table-text'>{locale['table0033'] /*暂无数据*/}</span>
                    </div>
                </td>
            </tr>];
        }

        /**
         * 包含展开行：展开行影响虚拟滚动
         */
        let { verticalVirtualInfo, horizontalVirtualInfo, allRows } = this.useVirtualScroll();

        let { 
                startRenderIndex: leftViewIndex, 
                endRenderIndex: rightViewIndex,
                topBlankCount: leftBlankCount,
                bottomBlankCount: rightBlankCount,
            } = horizontalVirtualInfo,
            { 
                startRenderIndex: topViewIndex, 
                endRenderIndex: bottomViewIndex,
                topBlank, 
                bottomBlank,
            } = verticalVirtualInfo;

        let renderRows;

        /**
         * 合并单元格
         * PS：与展开行互斥
         */
        if (mergeCell) {
            ({ 
                topBlank, 
                bottomBlank,
                renderRows,
            } = this.useMergeCell({ 
                // 原始的虚拟滚动信息
                topViewIndex,
                bottomViewIndex,
                leftViewIndex,
                rightViewIndex,
                leftBlankCount,
                rightBlankCount,
                // 需要更新的属性
                topBlank, 
                bottomBlank,
            }));
        } else {
            renderRows = allRows.slice(topViewIndex, bottomViewIndex + 1);
        }

        // 上下占位行
        topBlank > 0 && renderRows.unshift(() => <tr key='_top_blank_' className="blank-row top-blank" style={{ height: topBlank }}></tr>);
        bottomBlank > 0 && renderRows.push(() => <tr key='_bottom_blank_' className="blank-row bottom-blank" style={{ height: bottomBlank }}></tr>);
        return renderRows;
    }

    useVirtualScroll = () => {
        let { 
            rows, 
            rowsHeight,
            bufferSize,
            rowHeight,
            expandConfig = {},
            autoRowHeight,
            tableRef,
        } = this.props;

        let scrollTop = tableRef.current?.scrollTop ?? 0,
            bodyHeight = this.getBodyHeight();

        let {
            expandedKeys = new Set(),
            enable,
        } = expandConfig;
    
        let isExpandable = enable && expandedKeys.size > 0,
            finalRowsHeight = autoRowHeight ? this.innerRowsHeight : rowsHeight,
            isVariableHeight = finalRowsHeight && Object.keys(finalRowsHeight).length || isExpandable;

        let horizontalVirtualInfo = this.getHorizontalVirtualInfo();

        /**
         * allRows: RenderFunction[]
         */
        let allRows = [], verticalVirtualInfo;
        let rowsHeightArray = [];
        
        rows.forEach((row, rowIndex) => {
            allRows.push(() => this.renderRow(row, rowIndex, { 
                leftSpan: horizontalVirtualInfo.topBlankCount, 
                leftRenderIndex: horizontalVirtualInfo.startRenderIndex, 
                rightRenderIndex: horizontalVirtualInfo.endRenderIndex, 
                rightSpan: horizontalVirtualInfo.bottomBlankCount,
            }));
            
            if (isVariableHeight) {
                let rowHeight = this.getRowHeight(row, rowIndex);
                rowsHeightArray.push(rowHeight);
            }
            if (isExpandable) {
                ({ rowsHeightArray, allRows } = this.useExpandedRow(row, rowIndex, { rowsHeightArray, allRows }));
            }
            // TODO:把mergeCell加进来
        });

        if (isVariableHeight) {
            // 动态行高
            verticalVirtualInfo = calcVariableVirtualInfo({ 
                offset: scrollTop, // 滚动偏移量
                visibleSize: bodyHeight, // 可滚动区域
                bufferSize, // 缓冲区
                itemsSize: rowsHeightArray, // 每一项的大小
            });
        } else {
            // 固定行高
            verticalVirtualInfo = calcFixedVirtualInfo({ 
                offset: scrollTop, 
                visibleSize: bodyHeight, 
                bufferSize, 
                itemSize: rowHeight,
                itemCount: rows.length,
            });
        }

        this.tableHeight = verticalVirtualInfo.totalSize;

        return { verticalVirtualInfo, horizontalVirtualInfo, allRows };
    }

    useExpandedRow = (row, rowIndex, { rowsHeightArray, allRows }) => {
        let { expandConfig = {} } = this.props;

        let {
            expandedKeys = new Set(),
            expandable = () => true,
        } = expandConfig;

        let rowKey = this.getRowKey(row, rowIndex);

        if (expandable(row, rowIndex) && expandedKeys.has(rowKey)) {
            let expandedRowHeight = this.getExpandedRowHeight(row, rowIndex);

            rowsHeightArray.push(expandedRowHeight);
            allRows.push(() => this.renderExpandRow(row, rowIndex));
        }
        return { rowsHeightArray, allRows };
    }

    /**
     * 基于「合并单元格」，更新原始的虚拟滚动信息，渲染新的虚拟滚动行
     */
    useMergeCell = ({ 
        // 原始的虚拟滚动信息
        topViewIndex,
        bottomViewIndex,
        leftViewIndex,
        rightViewIndex,
        leftBlankCount,
        rightBlankCount,
        // 需要更新的属性
        topBlank, 
        bottomBlank,
    }) => {
        let { 
            rows, 
            columnInfo,
        } = this.props;

        let renderRows = [];
        let { leaf: { columns: leafColumns } } = columnInfo,
            topRenderIndex = -1, // 第一个渲染的行
            bottomRenderIndex = bottomViewIndex; // 最后一个渲染的行
            
        for (let rowIndex = 0; rowIndex <= bottomRenderIndex; rowIndex++) {
            let row = rows[rowIndex];
    
            let leftRenderIndex = leftViewIndex, // 第一个渲染的列
                rightRenderIndex = rightViewIndex, // 最后一个渲染的列
                leftSpan = leftBlankCount, // 左侧虚拟占位数
                rightSpan = rightBlankCount; // 右侧虚拟占位数
    
            // 基于「合并单元格」重新计算每行的虚拟滚动信息
            for (let colIndex = 0; colIndex <= rightViewIndex; colIndex++) {
                let column = leafColumns[colIndex];
    
                let { rowSpan = 1, colSpan = 1 } = column.mergeInfo?.(row, rowIndex) ?? {};

                this.spanManager.add({ 
                    rowIndex, 
                    colIndex, 
                    rowSpan,
                    colSpan,
                });

                /**
                 * 计算第一个需要渲染的行
                 * PS：这个计算需要前置，否则会有回溯的情况。所以实现方式和「计算最后一个需要渲染的行」不一致
                 */
                if (topRenderIndex < 0 && rowIndex <= topViewIndex && rowIndex + rowSpan > topViewIndex) {
                    topRenderIndex = rowIndex;
                }
    
                // 计算最后一个需要渲染的行
                if (rowIndex === bottomViewIndex) {
                    let spanInfo = this.spanManager.match({ rowIndex, colIndex });
                    if (spanInfo) {
                        bottomRenderIndex = Math.min(
                            Math.max(bottomRenderIndex, bottomViewIndex + spanInfo.rowSpan - 1), 
                            rows.length - 1,
                        );
                    }
                }
    
                // 计算第一个需要渲染的列
                if (colIndex === leftViewIndex) {
                    let spanInfo = this.spanManager.match({ rowIndex, colIndex });
                    if (spanInfo) {
                        let offset = leftRenderIndex - spanInfo.baseColIndex;
                        leftRenderIndex -= offset;
                        leftSpan = Math.max(0, leftSpan - offset);
                    } 
                }
    
                // 计算最后一个需要渲染的列
                if (colIndex === rightViewIndex) {
                    let spanInfo = this.spanManager.match({ rowIndex, colIndex });
                    if (spanInfo) {
                        let offset = spanInfo.baseColIndex + spanInfo.colSpan - 1 - rightRenderIndex;
                        rightRenderIndex += offset;
                        rightSpan = Math.max(0, rightSpan - offset);
                    } 
                }
            }
    
            // 基于「合并单元格」渲染虚拟滚动行
            if (topRenderIndex > -1) {
                if (rowIndex >= topRenderIndex && rowIndex < topViewIndex) {
                    // 填充顶部占位信息
                    topBlank -= this.getRowHeight(row, rowIndex);
                    renderRows.push(() => this.renderSpanBlankRow(row, rowIndex));
                } else if (rowIndex >= topViewIndex && rowIndex <= bottomViewIndex) {
                    // 更新视口渲染区
                    renderRows.push(() => this.renderRow(row, rowIndex, { leftSpan, leftRenderIndex, rightRenderIndex, rightSpan }));
                } else if (rowIndex > bottomViewIndex && rowIndex <= bottomRenderIndex) {
                    // 填充底部占位信息
                    bottomBlank -= this.getRowHeight(row, rowIndex);
                    renderRows.push(() => this.renderSpanBlankRow(row, rowIndex));
                }
            }
        }
            
        return { topBlank, bottomBlank, renderRows };
    }

    renderRow = (row, rowIndex, { leftSpan, leftRenderIndex, rightRenderIndex, rightSpan }) => {
        let { 
            rowProps: getRowProps, 
            columnInfo,
            isFixLeft, 
            isFixRight, 
            tabIndex = 0,
            autoRowHeight,
        } = this.props;

        let { 
            left: { leafColumns: leftColumns, offsets: leftOffsets }, 
            right: { leafColumns: rightColumns, offsets: rightOffsets }, 
            leaf: { columns: leafColumns }, 
        } = columnInfo;

        let leftCount = leftColumns.length;

        let rowHeight = this.getRowHeight(row, rowIndex),
            rowKey = this.getRowKey(row, rowIndex),
            rowProps = getRowProps?.(row, rowIndex);

        rowProps = {
            ...rowProps,
            className: classnames(rowProps?.className, { 
                'auto-row-height': autoRowHeight,
                'odd': rowIndex % 2 === 0,
                'even': rowIndex % 2 === 1,
            }),
        };

        if (autoRowHeight) {
            // 给0吧，td会被内容撑开不受影响，如果给auto，ie会出问题（高度起不来，单元格的边框不能撑满）
            // ie单元格正在输入时，高度无法立即响应，牺牲一下吧
            rowHeight = 0;
        }
        
        let tr = <tr 
            {...rowProps}
            key={rowKey} 
            rowKey={rowKey} 
            // style={{ height: rowHeight }}
            // tabIndex={tabIndex}
        >
            {[].concat(
                // 左侧固定列
                leftColumns.map(column => {
                    let colIndex = column.leafIndex;
                    let cellInfo = this.calcCellInfo({ row, rowIndex, column, colIndex });
                    return cellInfo && createElement('td', {
                        ...cellInfo,
                        className: classnames(
                            'table-next-body-cell', 
                            'fixed-left', 
                            { 
                                'is-fixing': isFixLeft,
                                'last-left-column': colIndex === leftCount - 1,
                            },
                            cellInfo.className,
                            'sticky',
                        ),
                        style: { 
                            ...cellInfo.style,
                            left: leftOffsets[colIndex],
                            height: rowHeight,
                        },
                    });
                }), 
                // 左侧虚拟占位
                leftSpan > 0 ? <td key="_left_blank_" className="_left_blank_" colSpan={leftSpan}></td> : [], 
                // 中部渲染区
                leafColumns.slice(leftRenderIndex, rightRenderIndex + 1).map(column => {
                    let cellInfo = this.calcCellInfo({ row, rowIndex, column, colIndex: column.leafIndex });
                    return cellInfo && createElement('td', {
                        ...cellInfo,
                        style: {
                            ...cellInfo.style,
                            height: rowHeight,
                        },
                    });
                }), 
                // 右侧虚拟占位
                rightSpan > 0 ? <td key="_right_blank_" className="_right_blank_" colSpan={rightSpan}></td> : [], 
                // 右侧固定列
                rightColumns.map((column, index) => {
                    let colIndex = column.leafIndex;
                    let cellInfo = this.calcCellInfo({ row, rowIndex, column, colIndex });
                    return cellInfo && createElement('td', {
                        ...cellInfo,
                        className: classnames(
                            'table-next-body-cell', 
                            'fixed-right', 
                            { 
                                'is-fixing': isFixRight,
                                'first-right-column': index === 0,
                            },
                            cellInfo.className,
                            'sticky',
                        ),
                        style: { 
                            ...cellInfo.style, 
                            right: rightOffsets[index], 
                            height: rowHeight,
                        },
                    });
                }), 
            )}
        </tr>;

        if (autoRowHeight) {
            return <ResizeObserver
                onResize={({ height }) => {
                    this.innerRowsHeight[rowKey] = height;
                }}
            >
                {tr}
            </ResizeObserver>;
        } else {
            return tr;
        }

    }

    renderExpandRow = (row, rowIndex) => {
        let { 
            expandConfig = {},
            tableBodyWidth,
            columnInfo,
            rowResizable,
        } = this.props;

        let { leaf: { columns: leafColumns } } = columnInfo;

        let { render: expandedRowRender, fixed } = expandConfig;

        let expandedRowKey = this.getExpandedRowKey(row, rowIndex),
            expandedRowHeight = this.getExpandedRowHeight(row, rowIndex);
    
        return <tr 
            key={expandedRowKey}
            style={{ height: expandedRowHeight }} 
            className="expanded-row"
        >
            <td colSpan={leafColumns.length} style={{ padding: 0 }}>
                {fixed ? <div 
                    style={{ 
                        width: tableBodyWidth, 
                        height: expandedRowHeight, 
                    }}
                    className="expanded-fixed-wrapper"
                >
                    {expandedRowRender(row, rowIndex)}
                </div> : expandedRowRender(row, rowIndex)}
                {rowResizable && this.renderDragHandler(row, rowIndex, true)}
            </td>
        </tr>;
    }

    getHorizontalVirtualInfo = () => {
        let { 
            horizontalVirtualInfo,
            columnInfo,
        } = this.props;

        let { left: { leafColumns: leftColumns } } = columnInfo;

        let leftCount = leftColumns.length;

        // 基于固定列的偏移量，更新横向虚拟滚动信息
        let properties = new Set(['startViewIndex', 'endViewIndex', 'startRenderIndex', 'endRenderIndex']);
        horizontalVirtualInfo = { ...horizontalVirtualInfo };
        for (let key in horizontalVirtualInfo) {
            if (properties.has(key)) {
                horizontalVirtualInfo[key] += leftCount;
            }
        }
        return horizontalVirtualInfo;
    }

    calcCellInfo = ({ row, rowIndex, column, colIndex }) => {
        let { 
                mergeCell, 
                expandConfig = {}, 
                rowResizable,
                cellProps: getCellProps,
                columnInfo,
                rows,
            } = this.props,
            { 
                enable,
                expandable = () => true, 
                expandedKeys = new Set(), 
                onExpand, 
            } = expandConfig;

        let { leaf: { columns: leafColumns } } = columnInfo;
            
        let rowKey = this.getRowKey(row, rowIndex);
    
        let spanInfo;
        if (mergeCell) {
            spanInfo = this.spanManager.match({ rowIndex, colIndex });
            if (spanInfo && !spanInfo.current) {
                return;
            }
        }

        let { key, render } = column,
            children = render?.(row, rowIndex),
            cellProps = getCellProps?.({ row, rowIndex, rows }, { col: column, columns: leafColumns, colIndex }) ?? {},
            { rowSpan = 1, colSpan = 1 } = spanInfo || {};

        if (colIndex === 0) {
            children = <>
                {enable && expandable?.(row, rowIndex) && <i 
                    className={`table-row-expand-icon ${expandedKeys.has(rowKey) ? 'table-row-expand-icon-open' : 'table-row-expand-icon-close'}`} 
                    onClick={() => {
                        let isExpanded;
                        if (expandedKeys.has(rowKey)) {
                            isExpanded = false;
                            expandedKeys.delete(rowKey);
                        } else {
                            isExpanded = true;
                            expandedKeys.add(rowKey);
                        }
                        onExpand?.(rowKey, { isExpanded, expandedKeys });
                    }}
                ></i>}
                {children}
                {rowResizable && this.renderDragHandler(row, rowIndex)}
            </>;
        }    

        return {
            ...cellProps,
            key,
            rowSpan, 
            colSpan,
            children,
            rowKey,
            colKey: key,
            fieldid: getSysFieldid(key),
        };
    }

    /**
     * 合并单元格时，渲染合并单元格的占位行
     * 只有合并单元格需要渲染内容
     */
    renderSpanBlankRow = (row, rowIndex) => {
        let { columnInfo } = this.props;
    
        let { leaf: { columns } } = columnInfo;

        let tds = [], 
            colSpan = 0;

        for (
            let colIndex = 0;
            colIndex < columns.length;
            colIndex++
        ) {
            let spanInfo = this.spanManager.match({ rowIndex, colIndex });
            if (spanInfo) {
                colSpan > 0 && tds.push(<td colSpan={colSpan} />);
                colSpan = 0;
                if (spanInfo.current) {
                    tds.push(
                        <td
                            rowSpan={spanInfo.rowSpan} 
                            colSpan={spanInfo.colSpan} 
                        >
                            {columns[colIndex].render?.(row, rowIndex)}
                        </td>,
                    );
                }
            } else {
                colSpan++;
            }
        }
        colSpan > 0 && tds.push(<td colSpan={colSpan} />);
        
        return <tr style={{ height: this.getRowHeight(row, rowIndex) }}>
            {tds}
        </tr>;
    }

    // 把手控制的是上一行的高度
    // 由于样式问题（会被position: sticky覆盖），把手的上一半由当前行渲染，下一半由下一行渲染
    // 每个单元格中渲染的把手，上半部分控制上一列，下半部分控制当前行
    renderDragHandler = (row, rowIndex, isExpandedRow) => {
        let { 
                rows, 
                onRowResize,
                rowsHeight,
                expandConfig,
                // columnInfo,
                autoRowHeight,
            } = this.props,
            { currentIndex } = this.state;

        let finalRowsHeight = autoRowHeight ? this.innerRowsHeight : rowsHeight;

        let { enable, expandable = () => true, expandedKeys = new Set() } = expandConfig;
        let isExpand = false;

        let prevIndex = -1, prevRow = null;

        // 第一行没有上把手
        prevIndex = isExpandedRow ? rowIndex : rowIndex - 1;
        prevRow = rows[prevIndex];
        isExpand = prevRow && enable && expandable?.(prevRow, prevIndex) && expandedKeys.has(this.getRowKey(prevRow, prevIndex));
        
        let handlerConfig = [
            { className: 'second-half-handler', row: prevRow, isExpand: !isExpandedRow && isExpand }, 
            // { className: 'first-half-handler', row: rows[rowIndex], isExpand: isExpandedRow },
        ];

        return handlerConfig.map(({ className, row, isExpand }) => {
            if (row) {
                // eslint-disable-next-line react/jsx-key
                return <div 
                    className={classnames('row-resize-handler', className, { 'current-resizing-row': rowIndex === currentIndex })}
                    onMouseDown={e => {
                        e.stopPropagation();

                        this.setState({ currentIndex: rowIndex });

                        let lastCursor = document.body.style.cursor;
                        document.body.style.cursor = 'row-resize';

                        this.startY = e.screenY;
                        this.startHeight = isExpand ? this.getExpandedRowHeight(row, rowIndex) : this.getRowHeight(row, rowIndex);
                        finalRowsHeight = finalRowsHeight || {};
                        const resize = e => {
                            e.stopPropagation();
                            e.preventDefault();
                            let rowKey = isExpand ? this.getExpandedRowKey(row, rowIndex) : this.getRowKey(row, rowIndex);
                            finalRowsHeight[rowKey] = Math.max(30, this.startHeight + e.screenY - this.startY);
                            onRowResize?.(finalRowsHeight);
                        };
                        
                        const finished = () => {
                            this.setState({ currentIndex: -1 });

                            document.body.style.cursor = lastCursor;
                            window.removeEventListener('mousemove', resize);
                            window.removeEventListener('mouseup', finished);
                        };

                        window.addEventListener('mousemove', resize);
                        window.addEventListener('mouseup', finished);
                    }}
                    // style={{ width: columnInfo.leaf.columns[0].width }}
                ></div>;
            }
        });
    }

    renderVerticalScrollBar = () => {
        let { verticalScrollBarRef, hasVerticalScrollBar, scrollBarSize, bodyStyle } = this.props;

        !hasVerticalScrollBar && (scrollBarSize = 0);

        return <div 
            ref={verticalScrollBarRef}
            style={{ 
                height: bodyStyle?.height, 
                maxHeight: bodyStyle?.maxHeight, 
                width: scrollBarSize,
            }}
            className="vertical-scroll-bar" 
            onScroll={this.virtualScroll}
        >
            <div className="vertical-scroll-item" style={{ height: this.tableHeight, width: scrollBarSize }}></div>
        </div>;
    }

    getRowHeight = (row, rowIndex) => {
        let { 
                rowHeight, 
                rowsHeight, 
                autoRowHeight,
            } = this.props,
            rowKey = this.getRowKey(row, rowIndex);

        return (autoRowHeight ? this.innerRowsHeight : rowsHeight)?.[rowKey] ?? rowHeight;
    }

    getRowKey = (row, rowIndex) => {
        let { rowKey } = this.props;
        if (!rowKey) {
            rowKey = row => row.key;
            console.warn("请提供rowKey属性！当前默认为：row => row.key");
        }
        return rowKey(row, rowIndex);
    }

    getExpandedRowKey = (row, rowIndex) => {
        return `${this.getRowKey(row, rowIndex)}-expanded`;
    }

    getExpandedRowHeight = (row, rowIndex) => {
        let { rowsHeight, expandConfig } = this.props,
            { height } =expandConfig;

        let expandedRowKey = this.getExpandedRowKey(row, rowIndex);
        return rowsHeight?.[expandedRowKey] || height;
    }

    getBodyHeight = () => {
        let { flex, bodyStyle, tableRef } = this.props;

        if (flex) {
            return bodyStyle?.height ?? 300;
        } else {
            return Math.min(
                typeof bodyStyle?.height === 'number' ? bodyStyle?.height : tableRef.current?.clientHeight ?? 0, 
                bodyStyle?.maxHeight ?? Infinity,
            );
        }
    }

    virtualScroll = event => {
        let { currentTarget } = event;
        let { scrollTop } = currentTarget;
        this.props.tableRef.current.scrollTop = scrollTop;
        this.setState({ scrollTop });
    }
}

class SpanManager {
    spanInfo = {}

    add({ rowIndex, colIndex, rowSpan = 1, colSpan = 1 }) {
        let spanInfo = this.match({ rowIndex, colIndex });
        // 没收集过的再收集
        if (!spanInfo && rowSpan > 1 || colSpan > 1) {
            for (let i = rowIndex; i < rowIndex + rowSpan; i++) {
                this.spanInfo[i] = this.spanInfo[i] || {};
                for (let j = colIndex; j < colIndex + colSpan; j++) {
                    this.spanInfo[i][j] = {
                        baseRowIndex: rowIndex,
                        baseColIndex: colIndex,
                        rowSpan, 
                        colSpan,
                        current: i === rowIndex && j === colIndex,
                    };
                }
            }
        }
    }

    match({ rowIndex, colIndex }) {
        return this.spanInfo?.[rowIndex]?.[colIndex] ?? null;
    }
}